/**
 * Where to show a `[Buffer]` hint over a binary request's body in the
 * transformation editor (JSON text). Binary bytes aren't JSON, so the body is
 * `null` in the editor while the transformation receives (or returned) a
 * Buffer: the hint replaces that `null`, styled like the multipart
 * `content: [Buffer]` hint.
 *
 * A `null` body is binary unless it is JSON `null`: every non-JSON content type
 * needs a string (text, XML, any other type) or an object (form, multipart), so
 * a `null` there stands for bytes. So does any body with a content-encoding.
 */
export const BINARY_BODY_HINT = '[Buffer]';

const headerValue = (headers: unknown, name: string): string | undefined => {
  if (!headers || typeof headers !== 'object') return undefined;
  const value = Object.entries(headers).find(([key]) => key.toLowerCase() === name)?.[1];
  return typeof value === 'string' ? value : undefined;
};

const isBinaryNullBody = (request: { headers?: unknown; body?: unknown }) => {
  if (request.body !== null) return false;
  if (headerValue(request.headers, 'content-encoding')) return true;
  const media_type = headerValue(request.headers, 'content-type')?.split(';')[0].trim();
  return !!media_type && !/json/i.test(media_type);
};

/** Offset of the top-level `body` value when it stands for binary bytes, if any. */
export const binaryBodyOffset = (text: string): number | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (_) {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || !isBinaryNullBody(parsed)) {
    return null;
  }

  // Find the "body" key at depth 1, then the `null` after its colon.
  let depth = 0;
  let i = 0;
  while (i < text.length) {
    const char = text[i];
    if (char === '{' || char === '[') depth += 1;
    else if (char === '}' || char === ']') depth -= 1;
    else if (char === '"') {
      const start = i;
      i += 1;
      while (i < text.length && text[i] !== '"') i += text[i] === '\\' ? 2 : 1;
      if (depth === 1 && text.slice(start, i + 1) === '"body"') {
        const match = /^\s*:\s*null/.exec(text.slice(i + 1));
        if (match) return i + 1 + match[0].length - 'null'.length;
      }
    }
    i += 1;
  }
  return null;
};

/** The hint for a binary body, as an editor inline hint replacing `null`. */
export const binaryBodyHints = (text: string) => {
  const offset = binaryBodyOffset(text);
  return offset === null ? [] : [{ offset, text: BINARY_BODY_HINT, replace: 'null'.length }];
};
